import { minidoc, h } from 'minidoc-editor';

export type Minidoc = ReturnType<typeof minidoc>;

const icoDelete = `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" class="w-4 h-4 opacity-75">
  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
</svg>
`;

/**
 * Find the leaf (direct child element of the editor root) that contains the
 * node. Returns undefined when the node is the root itself, or a bare
 * (non-element) node sitting directly under the root — i.e. there is no element
 * leaf to anchor to. Mirrors minidoc's own Dom.findLeaf: a range anchored where
 * this returns undefined makes minidoc's insert logic call
 * `setEndAfter(undefined)` and throw
 * "Argument 1 to Range.setEndAfter must be an instance of Node".
 */
function leafContaining(root: HTMLElement, node: Node | undefined): Element | undefined {
  let el: Element | undefined = node instanceof Element ? node : (node?.parentElement ?? undefined);
  while (el && el !== root) {
    if (el.parentElement === root) {
      return el;
    }
    el = el.parentElement ?? undefined;
  }
  return undefined;
}

/**
 * A collapsed range at the end of the editor's last leaf — a guaranteed
 * leaf-anchored caret. Used when the live/captured selection is missing or
 * anchored on the root (which happens after a native file dialog or after
 * clicking a card, on every browser).
 */
function caretAtEndOfEditor(root: HTMLElement): Range {
  let leaf = root.lastElementChild;
  if (!leaf) {
    leaf = document.createElement('p');
    root.appendChild(leaf);
  }
  const range = document.createRange();
  range.selectNodeContents(leaf);
  range.collapse(false);
  return range;
}

/**
 * Produce a safe range for inserting a media card. The range is always
 * COLLAPSED: minidoc's insert path runs `range.deleteContents()`, so a spanning
 * selection (e.g. select-all) would wipe the whole document and strand the
 * caret on the empty root — making `findLeaf` return undefined and crashing
 * `setEndAfter`. Collapsing to the start makes deleteContents a no-op (the
 * user's content is kept and the media is inserted at the caret). If the
 * collapsed caret isn't inside a leaf (it's on the root, or detached), it falls
 * back to a guaranteed leaf-anchored caret at the end of the editor.
 */
function safeInsertRange(root: HTMLElement, range: Range | undefined): Range {
  if (range && range.startContainer.isConnected) {
    const collapsed = range.cloneRange();
    collapsed.collapse(true);
    if (root.contains(collapsed.startContainer) && leafContaining(root, collapsed.startContainer)) {
      return collapsed;
    }
  }
  return caretAtEndOfEditor(root);
}

/**
 * Get the current range from the editor so that when we insert a file, it gets
 * inserted in the right place — collapsed and leaf-anchored (see
 * {@link safeInsertRange}).
 */
export function getCurrentRange(editor: Pick<Minidoc, 'root'>) {
  const sel = document.getSelection();
  const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : undefined;
  return safeInsertRange(editor.root, range);
}

/**
 * Restore the selection to the specified range. When an editor is passed, the
 * range is normalized via {@link safeInsertRange} (collapsed + leaf-anchored)
 * so the following insert can't empty the document or crash.
 */
export function setCurrentRange(range: Range, editor?: Pick<Minidoc, 'root'>) {
  const sel = document.getSelection();
  if (!sel) {
    return;
  }
  const effective = editor ? safeInsertRange(editor.root, range) : range;
  sel.removeAllRanges();
  sel.addRange(effective);
}

/**
 * This hackery is used to allow the user to navigate up into the title input
 * from the minidoc editor.
 */
export function keyboardEditorToTitle(e: KeyboardEvent, editor: Minidoc, titleSelector: string) {
  if (e.code === 'ArrowUp' || e.code === 'ArrowLeft') {
    const sel = document.getSelection()!;
    const range = sel.getRangeAt(0);
    if (!range.collapsed) {
      return;
    }
    const isStartOfDoc =
      range.startContainer === editor.root.firstElementChild && !range.startOffset;
    const selRect = range.getBoundingClientRect();
    const rootRect = editor.root.getBoundingClientRect();
    const topDiff = selRect.top - rootRect.top;
    const isFirstLine = isStartOfDoc || (topDiff > 0 && topDiff < 16);
    if (!isFirstLine) {
      return;
    }
    const isStartOfLine = isStartOfDoc || selRect.left - rootRect.left === 0;
    if (e.code === 'ArrowUp' || isStartOfLine) {
      e.preventDefault();
      document.querySelector<HTMLInputElement>(titleSelector)?.focus();
    }
  }
}

export function mediaContextMenu(opts: { label: string }, ...children: any[]) {
  return h(
    '.mini-context-wrapper.flex.justify-center.absolute.inset-x-0.-top-6.z-10',
    h('.mini-context-menu.whitespace-nowrap', ...children, btnDeleteMedia(opts)),
  );
}

function btnDeleteMedia(opts: { label: string }) {
  return h('button.mini-context-action.inline-flex.items-center.gap-2.p-2.px-4', {
    onclick: (e: any) => {
      e.preventDefault();
      const card = e.target.closest('mini-card');
      if (!card) {
        return;
      }
      const sel = document.getSelection()!;
      const next = card.nextSibling || card.parentElement;
      sel.selectAllChildren(next);
      card.nextSibling ? sel.collapseToStart() : sel.collapseToEnd();
      card.remove();
    },
    innerHTML: `${icoDelete} Delete ${opts.label}`,
  });
}
